import React, { useEffect, useState } from 'react';
import { Modal, ModalClose, Button, Icon } from '../../campaign-os';
import { campaignService, getErrorMessage } from '../../../../services/apiService';

/**
 * "Resend to not delivered" — the numbers first, then one confirm.
 *
 * Opens with a dry run of POST /campaigns/:id/resend, which writes nothing, and shows
 * exactly what the resend would do: how many mails go to how many vendors (a field with two
 * or three addresses now gets one personalised mail per address), and who will STILL get
 * nothing and why — those vendors need their record corrected, not another send. Only the
 * confirm button sends.
 */
const num = (n) => Number(n || 0).toLocaleString('en-US');

export const SKIP_LABELS = {
  placeholder: 'Placeholder address — no real email on the record',
  invalid: 'Malformed address',
  empty: 'No email on the record',
  'misspelt-domain': 'Misspelt domain (e.g. gamil.com)',
  bounced: 'Bounced before — the mailbox does not exist',
  unsubscribed: 'Unsubscribed',
  'no-mail-domain': 'The email domain does not exist',
};

/**
 * `scope` (optional) narrows the resend to one failure reason: `{ label, vendorIds }`, as the
 * campaign's Advanced analytics passes it. Without it, every vendor the campaign missed.
 */
export function ResendDialog({ open, campaignId, scope = null, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const only = scope ? { vendorIds: scope.vendorIds, label: scope.label } : {};

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setError(null);
    setLoading(true);
    campaignService
      .resend(campaignId, true, only)
      .then((res) => !cancelled && setPreview(res?.data ?? null))
      .catch((err) => !cancelled && setError(getErrorMessage(err) || 'Could not work out the resend'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId, scope]);

  const send = async () => {
    if (sending || !preview?.addresses) return;
    setSending(true);
    try {
      const res = await campaignService.resend(campaignId, false, only);
      onSent?.(res);
    } catch (err) {
      setError(getErrorMessage(err) || 'Could not queue the resend');
    } finally {
      setSending(false);
    }
  };

  const skipped = Object.entries(preview?.skipped_vendors ?? {}).sort((a, b) => b[1] - a[1]);
  const skippedTotal = skipped.reduce((n, [, v]) => n + v, 0);

  return (
    <Modal open={open} onClose={() => !sending && onClose()} size="md" labelledBy="resend-title">
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-cos-teal-faint">
              <Icon name="forward_to_inbox" className="text-[21px] text-cos-teal-deep" weight={500} />
            </span>
            <div>
              <h2 id="resend-title" className="text-[17px] font-bold tracking-tight text-cos-ink">
                {scope ? `Retry: ${scope.label}` : 'Retry failed sends — inside this campaign'}
              </h2>
              <p className="mt-1 max-w-[420px] text-[13px] leading-[19px] text-cos-ink-secondary">
                {scope && <>Only the vendors this reason stopped. </>}
                The same mail goes again to each vendor&apos;s <span className="font-semibold">current</span> email on record, from this
                campaign: as mails arrive, its own &quot;did not get it&quot; numbers go down. Anyone who already got it, or
                unsubscribed, is left out.
              </p>
            </div>
          </div>
          <ModalClose onClose={() => !sending && onClose()} />
        </div>

        {loading && (
          <p className="mt-6 flex items-center gap-2 text-2xs text-cos-ink-muted">
            <Icon name="progress_activity" className="animate-spin text-[14px]" />
            Working out who did not get it…
          </p>
        )}
        {error && (
          <p className="mt-5 rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg px-3 py-2.5 text-[12px] text-cos-status-coral">
            {error}
          </p>
        )}

        {preview && (
          <div className="mt-5 flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-cos-teal/30 bg-cos-teal-faint/60 px-4 py-3">
                <div className="mono text-[22px] font-bold text-cos-teal-deep">{num(preview.addresses)}</div>
                <div className="text-[12px] text-cos-ink-secondary">
                  mails to {num(preview.vendors_reached)} vendor{preview.vendors_reached === 1 ? '' : 's'}
                </div>
              </div>
              <div className="rounded-xl border border-cos-status-amber/30 bg-cos-status-amber-bg/40 px-4 py-3">
                <div className="mono text-[22px] font-bold text-cos-ink">{num(skippedTotal)}</div>
                <div className="text-[12px] text-cos-ink-secondary">vendors still cannot be reached</div>
              </div>
            </div>

            {preview.split_vendors > 0 && (
              <p className="flex items-start gap-1.5 text-[12px] leading-[17px] text-cos-ink-secondary">
                <Icon name="call_split" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
                {num(preview.split_vendors)} vendor{preview.split_vendors === 1 ? ' has' : 's have'} two or more addresses in
                one email field — each address gets its own mail.
              </p>
            )}

            {preview.copies_saved > 0 && (
              <p className="flex items-start gap-1.5 text-[12px] leading-[17px] text-cos-ink-secondary">
                <Icon name="group" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
                {num(preview.shared_addresses)} address{preview.shared_addresses === 1 ? ' is' : 'es are'} on more than one vendor
                record — one copy each ({num(preview.copies_saved)} fewer repeat mails).
              </p>
            )}

            {preview.shared_reached > 0 && (
              <p className="flex items-start gap-1.5 text-[12px] leading-[17px] text-cos-ink-secondary">
                <Icon name="mark_email_read" className="mt-px shrink-0 text-[15px] text-cos-teal-deep" />
                {num(preview.shared_reached)} vendor{preview.shared_reached === 1 ? '' : 's'} already got it at an address
                they share with another vendor record — not mailed again.
              </p>
            )}

            {skipped.length > 0 && (
              <div className="rounded-xl border border-cos-line bg-white/60 px-4 py-3">
                <div className="text-2xs font-semibold text-cos-ink">Not resent — fix these vendor records first</div>
                <ul className="mt-1.5">
                  {skipped.map(([reason, n]) => (
                    <li key={reason} className="flex items-baseline justify-between gap-3 py-0.5 text-[12px]">
                      <span className="text-cos-ink-secondary">{SKIP_LABELS[reason] ?? reason}</span>
                      <span className="mono font-bold text-cos-ink">{num(n)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] leading-[16px] text-cos-ink-muted">
              Already reached {num(preview.reached)} · unsubscribed {num(preview.unsubscribed)}
              {preview.awaiting > 0 && (
                <>
                  {' '}· <span className="font-semibold text-cos-ink-secondary">{num(preview.awaiting)} still waiting for a delivery result</span>{' '}
                  (not resent, so nobody gets it twice)
                </>
              )}
            </p>

            <div className="mt-1 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={sending} onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" icon="send" disabled={sending || !preview.addresses} onClick={send}>
                {sending ? 'Queuing…' : `Send ${num(preview.addresses)} mail${preview.addresses === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ResendDialog;
